import LightCustomButton from "@components/buttons/LightCustomButton";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import MobileTabSwitcher from "@components/switcher/MobileTabSwitcher";
import { billingServices } from "@services/apis/Billing";
import { useEffect, useRef, useState } from "react";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";
import AffiliateInvoices from "../../billing/AffiliateInvoices";
import Billing, { BillingHandle } from "../../billing/Billing";
import ConsumerInvoices from "../../billing/ConsumerInvoices";
import Expenses from "../../billing/Expenses";
import TradeReports from "../../billing/TradeReports";

const MobileBillingTabs = () => {
  const { handleError, handleSuccess } = useSnackbarMessage();

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [exportLoading, setExportLoading] = useState(false);

  const billingRef = useRef<BillingHandle | null>(null);
  const expensesRef = useRef<BillingHandle | null>(null);
  const consumerRef = useRef<BillingHandle | null>(null);
  const affiliatesRef = useRef<BillingHandle | null>(null);

  const [month, setMonth] = useState<string>("");
  const [genLoading, setGenLoading] = useState(false);

  const getCurrentMonthYYYYMM = () => {
    const now = new Date();
    const year = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    return `${year}-${mm}`;
  };

  useEffect(() => {
    setMonth(getCurrentMonthYYYYMM());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onGenerateMonthlyBilling = async () => {
    try {
      setGenLoading(true);

      const selectedMonth = month?.trim()
        ? month.trim()
        : getCurrentMonthYYYYMM();

      await billingServices.generateMonthlyBillingData({
        month: selectedMonth,
      });

      handleSuccess({
        message: `Request processed successfully for ${selectedMonth}`,
      });
    } catch (err: any) {
      handleError({ message: "Error processing the request" });
    } finally {
      setGenLoading(false);
    }
  };

  const tabRefs = [billingRef, expensesRef, consumerRef, affiliatesRef];

  const onExportBillingCSV = async () => {
    const activeRef = tabRefs[activeTabIndex];
    if (!activeRef?.current) {
      handleError({ message: "Table is not ready yet" });
      return;
    }
    try {
      setExportLoading(true);
      await activeRef.current.exportToCSV();
      handleSuccess({ message: "CSV exported successfully" });
    } catch {
      handleError({ message: "Failed to export CSV" });
    } finally {
      setExportLoading(false);
    }
  };

  const TRADE_REPORTS_TAB_INDEX = 4;

  const billingTabs = [
    { label: "Merchant", component: <Billing ref={billingRef} /> },
    { label: "Expenses", component: <Expenses ref={expensesRef} /> },
    { label: "Consumer Invoices", component: <ConsumerInvoices ref={consumerRef} /> },
    { label: "Affiliates", component: <AffiliateInvoices ref={affiliatesRef} /> },
    { label: "Trade Reports", component: <TradeReports /> },
  ];

  return (
    <div
      className="flex flex-col"
      style={{ height: "calc(100dvh - 56px)" }}
    >
      {/* Header */}
      <div className="border-b border-border-subtle bg-bg-page-body sticky top-0 z-10 px-4 py-3 shrink-0">
        <div className="flex flex-row justify-between items-center mb-3">
          <p className="text-text-base text-[14px] font-semibold">Billing</p>
          {activeTabIndex !== TRADE_REPORTS_TAB_INDEX && (
            <LightCustomButton
              text={exportLoading ? "EXPORTING..." : "EXPORT CSV"}
              onClick={onExportBillingCSV}
              isActive={!exportLoading}
              isButtonLoading={exportLoading}
            />
          )}
        </div>

        {/* Generate Monthly Billing controls - stacked for mobile */}
        <div className="flex flex-col gap-2 w-full">
          <GlobalTextInputField
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            placeholder="Month (YYYY-MM)"
            disabled={genLoading}
            showBorder={true}
            className="w-full"
          />
          <LightCustomButton
            text="GENERATE MONTHLY BILLING"
            onClick={onGenerateMonthlyBilling}
            isActive={!genLoading}
            isButtonLoading={genLoading}
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex-1 min-h-0 mt-3 mx-3 flex flex-col">
        <MobileTabSwitcher
          tabs={billingTabs}
          onTabChange={(index: number) => setActiveTabIndex(index)}
        />
      </div>
    </div>
  );
};

export default MobileBillingTabs;
